import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Toolbar,
  Typography,
  Tooltip,
  Badge,
  TablePagination,
} from '@mui/material';
import { Visibility as ViewIcon, History as HistoryIcon, Description as DescriptionIcon, Print as PrintIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import apiClient from '../services/apiClient';
import PermitModal from '../components/PermitModal';
import { isColumnVisible, getVisibleColumnCount, hasFeature } from '../utils/permissions';
import { formatDate } from '../utils/formatDate';
import PageLoader from '../components/PageLoader';

function PermitList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const orgContext = useOrg();
  const currentOrgCode = (orgContext?.org?.code || orgContext?.orgCode || localStorage.getItem('orgCode') || 'RTA').toUpperCase();
  const currentAuthority = orgContext?.org?.organization_type || currentOrgCode || 'RTA';
  const dateFormat = orgContext?.org?.settings?.date_format || 'DD/MM/YYYY';
  const [permits, setPermits] = useState([]);
  // Use a ref so setting it never recreates fetchPermits or re-triggers effects
  const isInitialLoadRef = useRef(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);

  // Initialize filters from URL search params so they persist across navigation
  const getInitialFilter = (key, fallback) => searchParams.get(key) || fallback;
  const [searchTerm, setSearchTerm] = useState(() => getInitialFilter('search', ''));
  const [statusFilter, setStatusFilter] = useState(() => getInitialFilter('status', 'pending'));
  const [authorityFilter, setAuthorityFilter] = useState(() => getInitialFilter('authority', currentAuthority));
  const [typeFilter, setTypeFilter] = useState(() => getInitialFilter('type', 'all'));
  const [assignedFilter, setAssignedFilter] = useState(() => getInitialFilter('assigned', 'all'));
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [historyTabActive, setHistoryTabActive] = useState(false);
  const [showPrintCertificateOnOpen, setShowPrintCertificateOnOpen] = useState(false);
  const [permitTypes, setPermitTypes] = useState([]);
  const [assignedRoles, setAssignedRoles] = useState([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Tracks which permit's details/history is being fetched (shows inline spinner)
  const [loadingPermitId, setLoadingPermitId] = useState(null);

  // Debounce timer ref
  const debounceTimerRef = React.useRef(null);

  // Track if we've initialized the assigned filter to avoid resetting on every render
  const initializedAssignedFilterRef = React.useRef(false);
  // Track whether the search term has changed post-mount (skip initial debounce fire)
  const searchMountedRef = useRef(false);

  // Keep a ref to the latest filter values so fetchPermits can read them
  // without needing them in its dependency array (avoids callback recreation).
  const filtersRef = useRef({ statusFilter: 'pending', authorityFilter: currentAuthority, typeFilter: 'all', assignedFilter: 'all', searchTerm: '', rowsPerPage: 20 });

  // Track the current page for the background poll (Effect 4 below) without
  // adding `page` to that effect's deps — avoids tearing down/recreating the
  // interval every time the user paginates.
  const pageRef = useRef(0);
  // Guards against a slow poll response overlapping the next tick.
  const isPollingRef = useRef(false);
  // Skip poll ticks while the view/edit modal is open, so a background
  // refresh never replaces the list data out from under an in-progress edit.
  const modalOpenRef = useRef(false);

  const fetchPermitTypes = useCallback(async () => {
    try {
      const response = await apiClient.get('/permit-types/');
      const types = response.data.results || response.data;
      console.log('Permit types fetched:', types);
      setPermitTypes(types);
    } catch (err) {
      console.error('Error fetching permit types:', err);
      setPermitTypes([]);
    }
  }, []);

  const fetchAssignedRoles = useCallback(async () => {
    try {
      const response = await apiClient.get('/roles/');
      let roles = response.data.results || response.data || [];

      // Ensure roles is an array
      if (!Array.isArray(roles)) {
        roles = [];
      }

      console.log('Roles fetched:', roles);
      setAssignedRoles(roles);
    } catch (err) {
      console.error('Error fetching roles:', err);
      setAssignedRoles([]);
    }
  }, []);

  // Sync filter state to URL search params so filters persist when navigating away and back
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (statusFilter && statusFilter !== 'pending') params.set('status', statusFilter);
    if (authorityFilter && authorityFilter !== 'all') params.set('authority', authorityFilter);
    if (typeFilter && typeFilter !== 'all') params.set('type', typeFilter);
    if (assignedFilter && assignedFilter !== 'all') params.set('assigned', assignedFilter);
    setSearchParams(params, { replace: true });
  }, [searchTerm, statusFilter, authorityFilter, typeFilter, assignedFilter, setSearchParams]);

  // Keep filtersRef in sync on every render so fetchPermits always reads fresh values.
  filtersRef.current = { statusFilter, authorityFilter, typeFilter, assignedFilter, searchTerm, rowsPerPage };
  pageRef.current = page;
  modalOpenRef.current = modalOpen;

  // Stable callback — empty deps so its reference NEVER changes.
  // Reads filters from filtersRef so it always uses the latest values.
  const fetchPermits = useCallback(async (pageNum = 0) => {
    const { statusFilter, authorityFilter, typeFilter, assignedFilter, searchTerm, rowsPerPage } = filtersRef.current;
    try {
      const params = {
        page: pageNum + 1,
        page_size: rowsPerPage,
      };

      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter && typeFilter !== 'all') params.permit_type = typeFilter;
      if (assignedFilter && assignedFilter !== 'all') {
        if (assignedFilter === 'unassigned') {
          params.assigned_to__isnull = 'true';
        } else {
          params.assigned_to = assignedFilter;
        }
      }
      if (searchTerm.trim()) params.search = searchTerm.trim();

      console.log('[PermitList] Fetching permits with params:', params);
      console.log('[PermitList] Current user:', user?.username, 'org_code:', user?.org_code, 'is_staff:', user?.is_staff);
      const response = await apiClient.get('/permits/', { params });
      console.log('[PermitList] Response status:', response.status, 'count:', response.data.count, 'results count:', response.data.results?.length);
      const data = response.data.results || response.data;

      setPermits(data);
      setTotalCount(response.data.count || 0);
      setError(null);

      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        setIsInitialLoad(false);
      }
    } catch (err) {
      setError(t('permitList.loadFailed'));
      console.error('[PermitList] Error:', err);
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        setIsInitialLoad(false);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effect 1: Initialise lookups when user logs in (never fetches permits).
  useEffect(() => {
    if (user?.id) {
      fetchPermitTypes();
      fetchAssignedRoles();
      if (!initializedAssignedFilterRef.current) {
        if (hasFeature(user, 'employee') && !user?.is_staff && user?.role?.id) {
          setAssignedFilter(String(user.role.id));
        }
        initializedAssignedFilterRef.current = true;
      }
    } else {
      setPermits([]);
      setAssignedFilter('all');
      initializedAssignedFilterRef.current = false;
    }
    // Deliberately keyed on user?.id rather than the whole `user` object —
    // matches every other effect in this file — since AuthContext's user
    // reference only changes on login/logout, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, fetchPermitTypes, fetchAssignedRoles]);

  // ── Effect 0: Restore filters from URL on mount (handles browser back/forward)
  // Only apply a URL value when it actually differs from current state —
  // otherwise this effect and the "sync filters -> URL" effect above can
  // ping-pong each other (URL change -> new searchParams identity -> this
  // effect re-fires -> setState -> URL sync fires again), and it can also
  // fight the Effect 1 employee auto-assign below over `assignedFilter`.
  useEffect(() => {
    if (!user?.id) return;
    const urlSearch = searchParams.get('search');
    const urlStatus = searchParams.get('status');
    const urlAuthority = searchParams.get('authority');
    const urlType = searchParams.get('type');
    const urlAssigned = searchParams.get('assigned');
    if (urlSearch !== null && urlSearch !== searchTerm) setSearchTerm(urlSearch);
    if (urlStatus !== null && urlStatus !== statusFilter) setStatusFilter(urlStatus);
    if (urlAuthority !== null && urlAuthority !== authorityFilter) setAuthorityFilter(urlAuthority);
    if (urlType !== null && urlType !== typeFilter) setTypeFilter(urlType);
    if (urlAssigned !== null && urlAssigned !== assignedFilter) setAssignedFilter(urlAssigned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, searchParams]);

  // ── Effect 2: Fetch on filter / page-size changes (immediate, no debounce).
  // fetchPermits is stable (empty deps) so this effect only fires when the
  // listed filter values actually change — never duplicates.
  useEffect(() => {
    if (!user?.id) return;
    setPage(0);
    fetchPermits(0);
  }, [statusFilter, authorityFilter, typeFilter, assignedFilter, rowsPerPage, user?.id, fetchPermits]);

  // ── Effect 3: Debounced search — skip the very first mount so it doesn't
  // double-fire alongside Effect 2 when the component initially renders.
  useEffect(() => {
    if (!searchMountedRef.current) {
      searchMountedRef.current = true;
      return;
    }
    if (!user?.id) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setPage(0);
      fetchPermits(0);
    }, 500);
    return () => clearTimeout(debounceTimerRef.current);
  }, [searchTerm, user?.id, fetchPermits]);

  // ── Effect 4: Background poll so newly created/updated permits show up
  // without a manual reload. Silent (no loading spinner, no filter/page
  // reset) — skips a tick if the previous poll is still in flight, the tab
  // is hidden, or the view/edit modal is open.
  useEffect(() => {
    if (!user?.id) return;
    const intervalId = setInterval(() => {
      if (document.hidden || isPollingRef.current || modalOpenRef.current) return;
      isPollingRef.current = true;
      fetchPermits(pageRef.current).finally(() => {
        isPollingRef.current = false;
      });
    }, 30000);
    return () => clearInterval(intervalId);
  }, [user?.id, fetchPermits]);

  const handleViewPermit = async (permit, isPrint = false) => {
    const permitId = permit.id || permit.pk;
    setLoadingPermitId(permitId);
    try {
      // Fetch full permit details including history
      const response = await apiClient.get(`/permits/${permitId}/`);
      setSelectedPermit(response.data);
      setIsEditMode(false);
      setShowPrintCertificateOnOpen(isPrint);
      setModalOpen(true);
    } catch (err) {
      console.error('Failed to load permit details:', err);
      // Fallback to showing the basic permit data
      setSelectedPermit(permit);
      setIsEditMode(false);
      setShowPrintCertificateOnOpen(isPrint);
      setModalOpen(true);
    } finally {
      setLoadingPermitId(null);
    }
  };

  const handleViewHistory = async (permit) => {
    const permitId = permit.id || permit.pk;
    setLoadingPermitId(permitId);
    try {
      // Fetch full permit details including history
      const response = await apiClient.get(`/permits/${permitId}/`);
      setSelectedPermit(response.data);
      setIsEditMode(false);
      setModalOpen(true);
      // Signal modal to open on History tab
      setTimeout(() => {
        setHistoryTabActive(true);
      }, 100);
    } catch (err) {
      console.error('Failed to load permit history:', err);
      setSelectedPermit(permit);
      setIsEditMode(false);
      setModalOpen(true);
      setTimeout(() => {
        setHistoryTabActive(true);
      }, 100);
    } finally {
      setLoadingPermitId(null);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedPermit(null);
    setHistoryTabActive(false);
    setShowPrintCertificateOnOpen(false);
  };

  const handleSavePermit = () => {
    setPage(0);
    fetchPermits(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchPermits(newPage);
    // Scroll to top of table
    document.querySelector('[role="table"]')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchPermits(0);
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'success',
      'inactive': 'default',
      'pending': 'warning',
      'cancelled': 'error',
      'expired': 'error',
    };
    return colors[status] || 'default';
  };

  const getLastAction = (permit) => {
    if (!permit.history || permit.history.length === 0) {
      return { action: t('permitList.created'), time: new Date(permit.issued_date) };
    }
    const lastRecord = permit.history[0];
    return {
      action: lastRecord.action.charAt(0).toUpperCase() + lastRecord.action.slice(1),
      time: new Date(lastRecord.timestamp)
    };
  };

  const getWorkflowInfo = (permit) => {
    const terminalMap = {
      active: { label: 'Approved', color: '#2e7d32', bg: '#e8f5e9' },
      cancelled: { label: 'Rejected', color: '#c62828', bg: '#ffebee' },
      expired: { label: 'Expired', color: '#e65100', bg: '#fff3e0' },
      inactive: { label: 'Suspended', color: '#616161', bg: '#f5f5f5' },
      draft: { label: 'Draft', color: '#9e9e9e', bg: '#fafafa' },
    };
    if (terminalMap[permit.status]) return terminalMap[permit.status];
    // pending — show the role it's currently waiting on
    const role = permit.assigned_to_role
      ? permit.assigned_to_role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      : 'Unassigned';
    return { label: role, color: '#1565c0', bg: '#e3f2fd' };
  };

  const getActionColor = (action) => {
    const colors = {
      'created': 'info',
      'updated': 'default',
      'activated': 'success',
      'deactivated': 'warning',
      'cancelled': 'error',
      'renewed': 'success',
    };
    return colors[action.toLowerCase()] || 'default';
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('common.justNow');
    if (diffMins < 60) return t('common.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('common.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('common.daysAgo', { count: diffDays });
    return formatDate(date, dateFormat);
  };

  if (isInitialLoad) {
    return <PageLoader variant="table" rows={5} />;
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Toolbar sx={{ pl: 0, pr: 0, mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
          {t('permitList.pageTitle')}
        </Typography>
        <Typography variant="caption" sx={{ color: '#666' }}>
          {t('permitList.countLabel', { shown: permits.length, total: totalCount, page: page + 1 })}
        </Typography>
      </Toolbar>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Search and Filters Section */}
      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr 1fr 1fr 1fr' }, gap: 2 }}>
          {/* Search Box */}
          <TextField
            label={t('permitList.searchLabel')}
            placeholder={t('permitList.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            variant="outlined"
            helperText={searchTerm ? t('permitList.searchHelper') : ''}
          />

          {/* Status Filter */}
          <TextField
            label={t('permitList.filterStatus')}
            select
            SelectProps={{ native: true }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            fullWidth
          >
            <option value="all">{t('permitList.allStatus')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="inactive">{t('common.inactive')}</option>
            <option value="pending">{t('common.pending')}</option>
            <option value="cancelled">{t('common.cancelled')}</option>
            <option value="expired">{t('common.expired')}</option>
          </TextField>

          {/* Authority Filter */}
          <TextField
            label={t('permitList.authority')}
            select
            SelectProps={{ native: true }}
            value={authorityFilter}
            size="small"
            fullWidth
            disabled={true}
          >
            <option value="all">{t('permitList.allAuthority')}</option>
            <option value="PTA">PTA</option>
            <option value="RTA">RTA</option>
          </TextField>

          {/* Type Filter */}
          <TextField
            label={t('permitList.permitType')}
            select
            SelectProps={{ native: true }}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            size="small"
            fullWidth
          >
            <option value="all">{t('permitList.allTypes')}</option>
            {permitTypes.map((type) => (
              <option key={type.id} value={type.code}>
                {type.name} ({type.code})
              </option>
            ))}
          </TextField>

          {/* Assigned To Filter - Show if user can manage/assign permits */}
          {(hasFeature(user, 'employee') || hasFeature(user, 'permit_update') || user?.is_staff) && (
            <TextField
              label={t('permitList.assignedTo')}
              select
              SelectProps={{ native: true }}
              value={assignedFilter}
              onChange={(e) => setAssignedFilter(e.target.value)}
              size="small"
              fullWidth
            >
              <option value="all">{t('permitList.allAssignments')}</option>
              <option value="unassigned">{t('permitList.unassigned')}</option>
              {assignedRoles.map((role) => (
                <option key={role.id} value={String(role.id)}>
                  {(role.name || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </option>
              ))}
            </TextField>
          )}
        </Box>

        {/* Active Filters Display */}
        {(searchTerm || statusFilter !== 'pending' || typeFilter !== 'all' || assignedFilter !== 'all') && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{t('permitList.activeFilters')}</Typography>
            {searchTerm && (
              <Chip
                label={t('permitList.searchChip', { term: searchTerm })}
                onDelete={() => setSearchTerm('')}
                size="small"
                color="primary"
              />
            )}
            {statusFilter !== 'pending' && (
              <Chip
                label={t('permitList.statusChip', { status: statusFilter === 'cancelled' ? t('common.cancelled') : statusFilter })}
                onDelete={() => setStatusFilter('pending')}
                size="small"
              />
            )}
            {authorityFilter !== 'all' && (
              <Chip
                label={t('permitList.authorityChip', { authority: authorityFilter })}
                size="small"
                disabled={true}
              />
            )}
            {typeFilter !== 'all' && (
              <Chip
                label={t('permitList.typeChip', { type: typeFilter })}
                onDelete={() => setTypeFilter('all')}
                size="small"
              />
            )}
            {(hasFeature(user, 'employee') || hasFeature(user, 'permit_update') || user?.is_staff) && assignedFilter !== 'all' && (
              <Chip
                label={t('permitList.assignedChip', { name: assignedFilter === 'unassigned' ? t('permitList.unassigned') : (assignedRoles.find(r => String(r.id) === String(assignedFilter))?.name?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || assignedFilter) })}
                onDelete={() => setAssignedFilter('all')}
                size="small"
              />
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('pending');
                setAuthorityFilter(currentAuthority);
                setTypeFilter('all');
                setAssignedFilter('all');
              }}
            >
              {t('permitList.clearAll')}
            </Button>
          </Box>
        )}
      </Paper>

      <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1565c0' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#1565c0' }}>{t('permitList.permitNumber')}</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#1565c0' }}>{t('permitList.vehicle')}</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#1565c0' }}>{t('permitList.owner')}</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#1565c0' }}>{t('permitList.authority')}</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#1565c0' }}>{t('permitList.type')}</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#1565c0' }}>{t('permitList.status')}</TableCell>
              {isColumnVisible(user, 'assignedTo') && <TableCell sx={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#1565c0' }}>{t('permitList.assignedTo')}</TableCell>}
              {isColumnVisible(user, 'lastAction') && <TableCell sx={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#1565c0' }}>{t('permitList.lastAction')}</TableCell>}
              <TableCell sx={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#1565c0', textAlign: 'center' }}>{t('permitList.renewalStatus')}</TableCell>
              {isColumnVisible(user, 'changes') && <TableCell sx={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#1565c0', textAlign: 'center' }}>{t('permitList.changes')}</TableCell>}
              {isColumnVisible(user, 'modified') && <TableCell sx={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#1565c0' }}>{t('permitList.modified')}</TableCell>}
              <TableCell sx={{ color: '#fff', fontWeight: 'bold', backgroundColor: '#1565c0', textAlign: 'center' }}>{t('permitList.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permits.length > 0 ? (
              permits.map((permit) => {
                const lastAction = getLastAction(permit);
                const historyCount = permit.history ? permit.history.length : 0;
                return (
                  <TableRow key={permit.id} hover sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                    <TableCell sx={{ fontWeight: '500' }}>{permit.permit_number}</TableCell>
                    <TableCell>{permit.vehicle_number}</TableCell>
                    <TableCell>{permit.owner_name}</TableCell>
                    <TableCell>{permit.authority}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      {permit.permit_type && typeof permit.permit_type === 'object'
                        ? permit.permit_type.name
                        : permit.permit_type}
                    </TableCell>
                    <TableCell>
                      <Chip
                        className="nastaliq-chip-padding"
                        label={permit.status === 'cancelled' ? t('common.cancelled') : permit.status === 'inactive' ? t('common.inactive') : (permit.status ? (t(`common.${permit.status}`, { defaultValue: permit.status })) : t('common.unknown'))}
                        color={getStatusColor(permit.status)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    {isColumnVisible(user, 'assignedTo') && (
                      <TableCell>
                        {permit.assigned_to_role ? (
                          <Tooltip title={t('permitList.assignedRoleTooltip', { name: permit.assigned_to_full_name || permit.assigned_to_role })}>
                            <Chip
                              className="nastaliq-chip-padding"
                              label={permit.assigned_to_full_name || (permit.assigned_to_role || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                              variant="outlined"
                              size="small"
                              color="primary"
                            />
                          </Tooltip>
                        ) : (
                          <span style={{ fontSize: '0.85rem', color: '#999' }}>{t('permitList.unassigned')}</span>
                        )}
                      </TableCell>
                    )}
                    {isColumnVisible(user, 'lastAction') && (
                      <TableCell>
                        <Tooltip title={new Date(lastAction.time).toLocaleString()}>
                          <Chip
                            className="nastaliq-chip-padding"
                            icon={<HistoryIcon />}
                            label={lastAction.action}
                            color={getActionColor(lastAction.action)}
                            variant="outlined"
                            size="small"
                          />
                        </Tooltip>
                      </TableCell>
                    )}
                    <TableCell sx={{ textAlign: 'center' }}>
                      {permit.has_previous_permits ? (
                        <Tooltip title={t('permitList.renewalTooltip', { numbers: permit.previous_permits?.map(p => p.permit_number).join(', ') || t('common.unknown') })}>
                          <Chip
                            className="nastaliq-chip-padding"
                            label={t('permitList.renewal')}
                            color="warning"
                            size="small"
                            sx={{
                              backgroundColor: '#ff9800',
                              color: 'white',
                              fontWeight: 'bold',
                              cursor: 'pointer'
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          className="nastaliq-chip-padding"
                          label={t('permitList.new')}
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    {isColumnVisible(user, 'changes') && (
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Badge
                          badgeContent={historyCount}
                          color="primary"
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.75rem',
                              height: '20px',
                              minWidth: '20px',
                              borderRadius: '50%',
                            }
                          }}
                        >
                          <Chip
                            className="nastaliq-chip-padding"
                            label={loadingPermitId === permit.id ? '' : t('permitList.history')}
                            icon={loadingPermitId === permit.id ? <CircularProgress size={14} color="inherit" /> : undefined}
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => loadingPermitId ? undefined : handleViewHistory(permit)}
                            sx={{
                              minWidth: '80px',
                              cursor: loadingPermitId === permit.id ? 'default' : 'pointer',
                              '&:hover': {
                                backgroundColor: '#e3f2fd',
                                fontWeight: 'bold'
                              }
                            }}
                          />
                        </Badge>
                      </TableCell>
                    )}
                    {isColumnVisible(user, 'modified') && <TableCell sx={{ fontSize: '0.85rem', color: '#666' }}>
                      {formatTimeAgo(lastAction.time)}
                    </TableCell>}
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Tooltip title={t('permitList.viewDetails')}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DescriptionIcon />}
                        onClick={() => navigate(`/${currentOrgCode.toLowerCase()}/permit-details/${permit.id}`)}
                          sx={{ mr: 1 }}
                        >
                          {t('permitList.details')}
                        </Button>
                      </Tooltip>
                      {permit.status === 'active' && (
                        <Tooltip title={t('permitList.printCertificate')}>
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              color="info"
                              startIcon={loadingPermitId === permit.id ? <CircularProgress size={14} color="inherit" /> : <PrintIcon />}
                              onClick={() => handleViewPermit(permit, true)}
                              disabled={loadingPermitId === permit.id}
                              sx={{ mr: 1 }}
                            >
                              {t('permitList.print')}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={getVisibleColumnCount(user)} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    {t('permitList.noPermits')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        sx={{ display: 'flex', justifyContent: 'flex-end', backgroundColor: '#f5f5f5', mt: 1 }}
      />

      <PermitModal
        open={modalOpen}
        permit={selectedPermit}
        onClose={handleModalClose}
        onSave={handleSavePermit}
        isEditMode={isEditMode}
        showHistoryTab={historyTabActive}
        showPrintCertificateOnOpen={showPrintCertificateOnOpen}
        defaultAuthority={currentAuthority}
      />
    </Box>
  );
}

export default PermitList;
